import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import moment from 'moment-timezone';
import { MemoryRouter } from 'react-router-dom';

import { useXProps } from './effects/useXProps';
import { ISOLATED_PAGE_APP_CONTAINER_ID } from './constants';
import { replaceUndefinedValuesWithNull } from './utils/replaceUndefinedValuesWithNull';
import { getDashboardCapturingHandler } from './utils/dashboardCapturing';

import { IsInsideIsolatedDcAppProvider } from '~/components/DevCenter/DevCenterAppContainer/components/IsInsideIsolatedDcAppProvider';
import DevCenterAppContainer from '~/components/DevCenter/DevCenterAppContainer';
import { IconParkProvider } from '~/icons';
import { withMUIProvidersHOC } from '~/hocs';
import AppContext from '~/components/DevCenter/AppContext';
import { THEMES } from '~/constants/theme';
import { LOCAL_STORAGE_APP_THEME_KEY } from '~/CLI/hocs/constants';
import { setThemeVariables } from '~/utils/themeVariables';
import { isPDFReportView } from '~/CLI/hocs/utils';
import { PermissionsContext } from '~/permissions/PermissionsContext';
import { updateUserUnits } from '~/utils';
import '~/styles/globalStyles';

const DevCenterAppContainerWithMUI = withMUIProvidersHOC(DevCenterAppContainer);

/**
 * It's important to keep these global styles in js, to apply only
 * when the component is actually used, if moved to CSS - they'll be
 * applied to whatever somehow imports the code
 */
const useStyles = makeStyles(() => ({
  '@global': {
    body: {
      backgroundColor: 'transparent',
      overflow: 'hidden',
    },
  },
}));

/**
 * Root component that is rendered inside of app's iframe.
 * Receives CLIAppComponent, CLIAppSettings when used
 * in local development
 */
export function DevCenterIsolatedAppPage({
  CLIAppComponent,
  CLIAppSettings,
  containerReportClassName,
}) {
  useStyles();

  const {
    appContextValue,
    globalMixpanelEventTracker,
    globalNotificationToastsAPI,
    onSettingChange,
    onSettingsChange,
    permissionsContextValue,
    timezone,
    userUnits,
    trackApiRateLimiterException,
    ...xProps
  } = useXProps();
  const isReportsPage = xProps.location.pathname.startsWith('/reports/') || isPDFReportView;
  const appId = xProps?.app?.id;

  // Force light theme for PDF reports view
  const selectedTheme =
    localStorage.getItem(LOCAL_STORAGE_APP_THEME_KEY) ||
    xProps.currentUser?.settings?.uiSettings?.theme ||
    THEMES.DARK;
  const isDarkTheme = !isReportsPage && selectedTheme !== THEMES.LIGHT;

  useEffect(() => {
    setThemeVariables(isDarkTheme);
  }, [isDarkTheme]);

  useEffect(() => {
    window[Symbol.for('notificationToasts')] = globalNotificationToastsAPI;
    window[Symbol.for('logToMixpanel')] = globalMixpanelEventTracker;
    window[Symbol.for('corvaAPI.rateLimiterReport')] = trackApiRateLimiterException;
  }, [globalNotificationToastsAPI, globalMixpanelEventTracker, trackApiRateLimiterException]);

  const isFirstRenderRef = useRef(true);
  if (isFirstRenderRef.current || isReportsPage) {
    isFirstRenderRef.current = false;
    updateUserUnits({ userUnits });
  }

  useEffect(() => {
    if (timezone) moment.tz.setDefault(timezone);
    return () => moment.tz.setDefault(null);
  }, [timezone]);

  useEffect(() => {
    const dashboardCapturingHandler = getDashboardCapturingHandler(appId);
    window.addEventListener('message', dashboardCapturingHandler);
    return () => window.removeEventListener('message', dashboardCapturingHandler);
  }, [appId]);

  return (
    <MemoryRouter
      initialEntries={[(xProps.location?.pathname || '/') + (xProps.location?.search || '')]}
    >
      <PermissionsContext.Provider value={permissionsContextValue}>
        <AppContext.Provider value={appContextValue}>
          <IsInsideIsolatedDcAppProvider value>
            <IconParkProvider>
              <div
                id={ISOLATED_PAGE_APP_CONTAINER_ID}
                className={classNames(isReportsPage && containerReportClassName)}
              >
                <DevCenterAppContainerWithMUI
                  {...xProps}
                  theme={isDarkTheme ? THEMES.DARK : THEMES.LIGHT}
                  // zoid serializes passed arguments using JSON.stringify.
                  // All undefined values are removed as they are not valid JSON values
                  // so we replace undefined values with null to not drop them
                  // Any future cb function that passes arbitrary objects as args
                  // also should be wrapped in this mapper
                  // ⚠️⚠️⚠️ When refactoring this to referentially stable callbacks, remove `useStableCallback` from
                  // /src/componentsV2/AppCommonsProvider/AppCommonsProvider.tsx
                  onSettingsChange={nextSettings =>
                    onSettingsChange(replaceUndefinedValuesWithNull(nextSettings))
                  }
                  onSettingChange={(key, value) =>
                    onSettingChange(key, replaceUndefinedValuesWithNull(value))
                  }
                  CLIAppComponent={CLIAppComponent}
                  CLIAppSettings={CLIAppSettings}
                  trackApiRateLimiterException={trackApiRateLimiterException}
                />
              </div>
            </IconParkProvider>
          </IsInsideIsolatedDcAppProvider>
        </AppContext.Provider>
      </PermissionsContext.Provider>
    </MemoryRouter>
  );
}

DevCenterIsolatedAppPage.propTypes = {
  CLIAppComponent: PropTypes.func,
  CLIAppSettings: PropTypes.func,
  containerReportClassName: PropTypes.string,
};

DevCenterIsolatedAppPage.defaultProps = {
  CLIAppComponent: null,
  CLIAppSettings: null,
  containerReportClassName: null,
};
